import React, { useEffect, useState } from "react";
import "../page/addProductInvoice.css";
import { MdDelete } from "react-icons/md";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";
import { formatRupiah, formatToIDR } from "../../../helper/formatNumber";

const AddProductInvoice = () => {
  // =====================
  // Constant Definition
  // =====================

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [SuggestionSupplier, setSuggestionSupplier] = useState([]);
  const [SuggestionProduct, setSuggestionProduct] = useState([]);
  const [activeInputIndex, setActiveInputIndex] = useState(null);
  const [productData, setProductData] = useState({
    product_invoice_id: "",
    supplier: {
      supplier_id: "",
      supplier_name: "",
    },
    product_invoice_document_number: "",
    product_invoice_date: new Date(),
    product_invoice_due_date: new Date(),
    product_invoice_products: [
      {
        product_invoice_product_id: "",
        product_invoice_product_name: "",
        product_invoice_product_batch: "",
        product_invoice_product_quantity: 0,
        product_invoice_product_expired_date: new Date(),
        product_invoice_product_price_per_unit: 0,
        product_invoice_product_discount: 0.0,
        product_invoice_total_product_price: 0,
      },
    ],
    product_invoice_total_discount: 0,
    product_invoice_total_order: 0,
    product_invoice_total_tax: 0,
    product_invoice_total_netto: 0,
    product_invoice_total_netto_tax: 0,
  });
  const [supplierTaxStatus, setSupplierTaxStatus] = useState("PKP");

  // =====================
  // Function Definition
  // =====================

  const formatDate = (date, isAsValue) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      alert("Invalid date provided");
      return "";
    }

    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Asia/Jakarta",
    };

    const result = new Intl.DateTimeFormat("id-ID", options).format(date);

    if (isAsValue) {
      const [day, month, year] = result.split("/");
      return `${year}-${month}-${day}`;
    }
    return result;
  };

  const formatDateToServer = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      alert("Invalid date provided");
      return "";
    }

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  const backToPreviousPage = () => {
    setProductData({
      product_invoice_id: "",
      product_invoice_supplier_id: "",
      product_invoice_document_number: "",
      product_invoice_date: new Date(),
      product_invoice_due_date: new Date(),
      product_invoice_products: [],
      product_invoice_total_discount: 0,
      product_invoice_total_order: 0,
      product_invoice_total_tax: 0,
      product_invoice_total_netto: 0,
      product_invoice_total_netto_tax: 0,
    });
    navigate("/produk-invoice", { replace: true });
  };

  const addProduct = () => {
    setProductData((prevData) => ({
      ...prevData,
      product_invoice_products: [
        ...prevData.product_invoice_products,
        {
          product_invoice_product_id: "",
          product_invoice_product_name: "",
          product_invoice_product_batch: "",
          product_invoice_product_quantity: 0,
          product_invoice_product_expired_date: new Date(),
          product_invoice_product_price_per_unit: 0,
          product_invoice_product_discount: 0,
          product_invoice_total_product_price: 0,
        },
      ],
    }));
  };

  const handleProductIdAndNameChange = (
    index,
    field1,
    value1,
    field2,
    value2
  ) => {
    const updatedProducts = productData.product_invoice_products.map(
      (product, i) => {
        if (i === index) {
          return {
            ...product,
            [field1]: value1, // Update the first specified field
            [field2]: value2, // Update the second specified field
          };
        }
        return product;
      }
    );
    setProductData((prevData) => ({
      ...prevData,
      product_invoice_products: updatedProducts,
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = productData.product_invoice_products.map(
      (product, i) => {
        if (i === index) {
          if (
            field === "product_invoice_product_price_per_unit" ||
            field === "product_invoice_product_quantity" ||
            field === "product_invoice_product_discount"
          ) {
            const updatedFieldValue =
              field === "product_invoice_product_price_per_unit"
                ? { product_invoice_product_price_per_unit: value }
                : field === "product_invoice_product_quantity"
                ? { product_invoice_product_quantity: value }
                : { product_invoice_product_discount: value };

            const pricePerUnit =
              field === "product_invoice_product_price_per_unit"
                ? value
                : product.product_invoice_product_price_per_unit;

            const quantity =
              field === "product_invoice_product_quantity"
                ? value
                : product.product_invoice_product_quantity;

            const discount =
              field === "product_invoice_product_discount"
                ? value
                : product.product_invoice_product_discount;

            const totalPrice = Math.ceil(
              pricePerUnit * quantity -
                pricePerUnit * quantity * (discount / 100)
            );

            return {
              ...product,
              ...updatedFieldValue,
              product_invoice_total_product_price: totalPrice,
            };
          } else {
            return {
              ...product,
              [field]: value,
            };
          }
        }
        return product;
      }
    );

    setProductData((prevData) => ({
      ...prevData,
      product_invoice_products: updatedProducts,
    }));
  };

  const checkSupplierAutoComplete = (keyword) => {
    if (keyword.length > 3) {
      findSupplier(keyword);
    } else {
      setSuggestionSupplier([]);
    }
  };

  const checkProductAutoComplete = (keyword) => {
    if (keyword.length > 3) {
      findProduct(keyword);
    } else {
      setSuggestionProduct([]);
    }
  };

  const removeProduct = (indexData) => {
    setProductData((prevData) => ({
      ...prevData,
      product_invoice_products: prevData.product_invoice_products.filter(
        (_, index) => index !== indexData
      ),
    }));
  };

  // =====================
  // Request Method
  // =====================

  const createNewProduct = async () => {
    try {
      const filledProduct = productData.product_invoice_products.filter(
        (product) => product.product_invoice_product_id
      );

      if (productData.supplier.supplier_id === "") {
        alert("Pastikan Data Supplier Sudah Terisi");
        return;
      }

      if (!filledProduct.length > 0) {
        alert("Produk Belum Diisi");
        return;
      }

      const productsWithFormattedDates = filledProduct.map(
        ({ product_invoice_product_expired_date, ...rest }) => ({
          ...rest,
          product_invoice_product_expired_date: formatDateToServer(
            product_invoice_product_expired_date
          ),
        })
      );

      const response = await fetch(Constant.BASE_API_URL + "/invoice-produk", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          product_invoice_supplier_id: productData.supplier.supplier_id,
          product_invoice_supplier_name: productData.supplier.supplier_name,
          product_invoice_document_number:
            productData.product_invoice_document_number,
          product_invoice_date: formatDateToServer(
            productData.product_invoice_date
          ),
          product_invoice_due_date: formatDateToServer(
            productData.product_invoice_due_date
          ),
          product_invoice_products: productsWithFormattedDates,
          product_invoice_total_discount:
            productData.product_invoice_total_discount,
          product_invoice_total_order: productData.product_invoice_total_order,
          product_invoice_total_tax: productData.product_invoice_total_tax,
          product_invoice_total_netto: productData.product_invoice_total_netto,
          product_invoice_total_netto_tax:
            productData.product_invoice_total_netto_tax,
        }),
      });

      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      } else {
        backToPreviousPage();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const findProduct = async (keyword) => {
    try {
      const response = await fetch(
        Constant.BASE_API_URL + "/produk/autocomplete?keyword=" + keyword,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;
      setSuggestionProduct(data.product);
    } catch (error) {
      setSuggestionProduct([]);
    }
  };

  const findSupplier = async (keyword) => {
    try {
      const response = await fetch(
        Constant.BASE_API_URL + "/supplier/autocomplete?keyword=" + keyword,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }

      const json = await response.json();
      const data = json.data;
      setSuggestionSupplier(data.supplier);
    } catch (error) {
      setSuggestionSupplier([]);
    }
  };

  // =====================
  // use effect Method
  // =====================

  useEffect(() => {
    // Compute updated totals
    const updatedTotals = productData.product_invoice_products.reduce(
      (totals, product) => {
        const {
          product_invoice_product_quantity: quantity,
          product_invoice_product_price_per_unit: pricePerUnit,
          product_invoice_product_discount: discountPercentage,
        } = product;

        const totalPrice = quantity * pricePerUnit;
        const discount = (totalPrice * discountPercentage) / 100;

        totals.totalOrder += totalPrice;
        totals.totalDiscount += discount;

        return totals;
      },
      { totalOrder: 0, totalDiscount: 0 }
    );

    const totalNetto = updatedTotals.totalOrder - updatedTotals.totalDiscount;
    var totalTax = 0;
    var totalNettoTax = 0;
    if (supplierTaxStatus === "PKP") {
      totalTax = Math.ceil(totalNetto * 0.11);
      totalNettoTax = totalNetto + totalTax;
    }

    setProductData((prevData) => ({
      ...prevData,
      product_invoice_total_order: updatedTotals.totalOrder,
      product_invoice_total_discount: updatedTotals.totalDiscount,
      product_invoice_total_netto: totalNetto,
      product_invoice_total_tax: totalTax,
      product_invoice_total_netto_tax: totalNettoTax,
    }));
  }, [productData.product_invoice_products, supplierTaxStatus]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1 className="header-invoice">Buat Faktur</h1>
      <div className="invoice-document-header">
        <input
          className="document-number-text-field"
          type="text"
          value={
            productData.product_invoice_document_number
              ? productData.product_invoice_document_number
              : ""
          }
          onChange={(e) =>
            setProductData({
              ...productData,
              product_invoice_document_number: e.target.value,
            })
          }
          placeholder="Masukkan nomor dokumen"
        />
        <div className="supplier-container">
          <input
            className="supplier-text-field"
            type="text"
            value={productData.supplier?.supplier_name || ""}
            onChange={(e) => {
              const value = e.target.value;
              setProductData((productData) => ({
                ...productData,
                supplier: {
                  ...productData.supplier,
                  supplier_name: value,
                },
              }));
              checkSupplierAutoComplete(value);
            }}
            placeholder="Masukkan nama Supplier"
          />
          {SuggestionSupplier.length > 0 && (
            <ul className="dropdown-list">
              {SuggestionSupplier.map((supplier) => (
                <li key={supplier.supplier_id}>
                  <button
                    className="dropdown-button"
                    onClick={() => {
                      setProductData((productData) => ({
                        ...productData,
                        supplier: {
                          supplier_name: supplier.supplier_name,
                          supplier_id: supplier.supplier_id,
                        },
                      }));
                      setSuggestionSupplier([]);
                      setSupplierTaxStatus(supplier.supplier_tax_status);
                    }}
                  >
                    {supplier.supplier_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="input-title-label-container">
          <span className="input-title-label">Tanggal</span>
        </div>
        <div className="input-patient-field-container">
          <input
            type="date"
            value={formatDate(productData.product_invoice_date, true)}
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              if (!isNaN(selectedDate.getTime())) {
                setProductData({
                  ...productData,
                  product_invoice_date: selectedDate,
                });
              }
            }}
            onInput={(e) => e.preventDefault()}
            placeholder="Masukkan Tanggal invoice"
          />
        </div>
        <div className="input-title-label-container">
          <span className="input-title-label">Jatuh Tempo</span>
        </div>
        <div className="input-patient-field-container">
          <input
            type="date"
            value={formatDate(productData.product_invoice_due_date, true)}
            onChange={(e) => {
              const selectedDate = new Date(e.target.value);
              if (!isNaN(selectedDate.getTime())) {
                setProductData({
                  ...productData,
                  product_invoice_due_date: selectedDate,
                });
              }
            }}
            onInput={(e) => e.preventDefault()}
            placeholder="Masukkan Tanggal invoice"
          />
        </div>
      </div>
      <div className="section-product-invoice">
        <table className="product-invoice-table">
          <thead>
            <tr>
              <th>Nama Produk</th>
              <th>Tanggal Kadaluwarsa</th>
              <th>Nomor Batch</th>
              <th>Kuantitas</th>
              <th>Harga Satuan</th>
              <th>Diskon (%)</th>
              <th>Total Harga</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {productData.product_invoice_products.map((product, index) => (
              <tr key={index}>
                <td>
                  <input
                    className="product-name"
                    type="text"
                    value={
                      product.product_invoice_product_name
                        ? product.product_invoice_product_name
                        : ""
                    }
                    onChange={(e) => {
                      handleProductChange(
                        index,
                        "product_invoice_product_name",
                        e.target.value
                      );
                      checkProductAutoComplete(e.target.value);
                    }}
                    onFocus={() => {
                      setActiveInputIndex(index);
                      setSuggestionProduct([]);
                    }}
                    placeholder="Masukkan nama product"
                  />
                  {SuggestionProduct.length > 0 &&
                    product.product_invoice_product_name !== "" &&
                    activeInputIndex === index && (
                      <ul className="dropdown-list">
                        {SuggestionProduct.map((products) => (
                          <li key={products.product_id}>
                            <button
                              className="dropdown-button"
                              onClick={() => {
                                handleProductIdAndNameChange(
                                  index,
                                  "product_invoice_product_id",
                                  products.product_id,
                                  "product_invoice_product_name",
                                  products.product_name
                                );
                                setSuggestionProduct([]);
                              }}
                            >
                              {products.product_name} | Stok Sisa:{" "}
                              <b>{products.product_total_stock}</b> | Harga
                              Beli:{" "}
                              <b>
                                {formatToIDR(products.product_buying_price)}
                              </b>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                </td>
                <td>
                  <input
                    className="product-expired-date"
                    type="date"
                    value={formatDate(
                      product.product_invoice_product_expired_date,
                      true
                    )}
                    onChange={(e) => {
                      const selectedDate = new Date(e.target.value);
                      if (!isNaN(selectedDate.getTime())) {
                        handleProductChange(
                          index,
                          "product_invoice_product_expired_date",
                          selectedDate
                        );
                      } else {
                        console.error("Invalid date value:", e.target.value);
                      }
                    }}
                    onInput={(e) => e.preventDefault()}
                    placeholder="Masukkan Tanggal invoice"
                  />
                </td>

                <td>
                  <input
                    className="product-batch-number"
                    type="text"
                    value={
                      product.product_invoice_product_batch
                        ? product.product_invoice_product_batch
                        : ""
                    }
                    onChange={(e) =>
                      handleProductChange(
                        index,
                        "product_invoice_product_batch",
                        e.target.value
                      )
                    }
                    placeholder="Nomor Batch"
                  />
                </td>
                <td>
                  <input
                    className="product-quantity"
                    type="text"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    value={
                      product.product_invoice_product_quantity
                        ? product.product_invoice_product_quantity
                        : ""
                    }
                    onChange={(e) =>
                      handleProductChange(
                        index,
                        "product_invoice_product_quantity",
                        e.target.value
                      )
                    }
                    placeholder="kuantitas"
                    onWheel={(e) => e.target.blur()}
                  />
                </td>
                <td>
                  <input
                    className="product-unit-price"
                    type="text"
                    value={
                      productData.product_invoice_products[index]
                        .product_invoice_product_price_per_unit
                        ? productData.product_invoice_products[index]
                            .product_invoice_product_price_per_unit
                        : ""
                    }
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    onChange={(e) =>
                      handleProductChange(
                        index,
                        "product_invoice_product_price_per_unit",
                        e.target.value
                      )
                    }
                    placeholder="Harga satuan produk"
                    onWheel={(e) => e.target.blur()}
                  />
                </td>
                <td>
                  <input
                    className="product-discount"
                    type="text"
                    value={
                      productData.product_invoice_products[index]
                        .product_invoice_product_discount
                        ? productData.product_invoice_products[index]
                            .product_invoice_product_discount
                        : ""
                    }
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9.]/g, ""); // Allow numbers and a single dot
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      let dotCount = (value.match(/\./g) || []).length;
                      if (dotCount > 1) {
                        value = value.slice(0, value.lastIndexOf("."));
                      }
                      let parts = value.split(".");
                      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "."); // Add dots for thousands

                      let formattedValue = parts.join(".");

                      handleProductChange(
                        index,
                        "product_invoice_product_discount",
                        formattedValue
                      );
                    }}
                    placeholder="Diskon"
                    onWheel={(e) => e.target.blur()}
                  />
                </td>
                <td>
                  <input
                    className="product-total-price"
                    type="text"
                    disabled={true}
                    value={
                      productData.product_invoice_products[index]
                        .product_invoice_total_product_price
                        ? formatRupiah(
                            Number(
                              productData.product_invoice_products[index]
                                .product_invoice_total_product_price
                            )
                          )
                        : ""
                    }
                    onChange={(e) =>
                      handleProductChange(
                        index,
                        "product_invoice_total_product_price",
                        e.target.value
                      )
                    }
                    placeholder="Total Harga"
                  />
                </td>
                <td>
                  {productData.length > 1 && (
                    <MdDelete
                      className="remove-product-list"
                      onClick={() => {
                        removeProduct(index);
                      }}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button
          className="add-more-product-item"
          type="button"
          onClick={addProduct}
        >
          Tambah Produk Lagi
        </button>
      </div>
      <div style={{ margin: 16 }}>
        <div className="sales-summary-container">
          <h3>Detail Pembelian</h3>
          <div className="sales-summary-transaction-table">
            <div className="sales-summary-row">
              <p className="sales-summary-column-title">Total Order :</p>
              <p className="sales-summary-column">
                <b>{formatRupiah(productData.product_invoice_total_order)}</b>
              </p>
            </div>
            <div className="sales-summary-row">
              <p className="sales-summary-column-title">Diskon Rp :</p>
              <p className="sales-summary-column">
                <b>
                  {formatRupiah(productData.product_invoice_total_discount)}
                </b>
              </p>
            </div>
            <div className="sales-summary-row">
              <p className="sales-summary-column-title">Netto :</p>
              <p className="sales-summary-column">
                <b>{formatRupiah(productData.product_invoice_total_netto)}</b>
              </p>
            </div>
            {supplierTaxStatus === "PKP" && (
              <div>
                <div className="sales-summary-row">
                  <p className="sales-summary-column-title">PPN :</p>
                  <p className="sales-summary-column">
                    <b>{formatRupiah(productData.product_invoice_total_tax)}</b>
                  </p>
                </div>
                <div className="sales-summary-row">
                  <p className="sales-summary-column-title">Netto PPN :</p>
                  <p className="sales-summary-column">
                    <b>
                      {formatRupiah(
                        productData.product_invoice_total_netto_tax
                      )}
                    </b>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ margin: 16 }}>
        <footer className="modal-product-footer">
          <button className="modal-close-button" onClick={backToPreviousPage}>
            Batal
          </button>

          <button
            className="modal-submit-button"
            onClick={() => {
              setIsLoading(true);
              createNewProduct();
            }}
          >
            Submit
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AddProductInvoice;
